import { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchStatuses } from '../api/dispatchService';
import { debug } from '../lib/debug';
import type { StatusItem } from '../types';

interface StatusEntry {
  code: string;
  name: string;
  bg_color?: string;
  text_color?: string;
  [key: string]: unknown;
}

interface RawStatuses {
  reservation?: Record<string, StatusEntry>;
  vehicle?: Record<string, StatusEntry>;
  vehicle_location?: Record<string, StatusEntry>;
  vehicle_type?: Record<string, StatusEntry>;
  [key: string]: Record<string, StatusEntry> | undefined;
}

/**
 * 予約ステータスを管理するフック
 * APIからステータスマスターを取得し、エディタ用のリスト形式に変換して提供する
 */
export const useReservationStatuses = () => {
    const [reservationStatusList, setReservationStatusList] = useState<StatusItem[]>([]);
    const [vehicleStatusList, setVehicleStatusList] = useState<StatusItem[]>([]);
    const [vehicleLocationStatusList, setVehicleLocationStatusList] = useState<StatusItem[]>([]);
    const [vehicleTypeList, setVehicleTypeList] = useState<StatusItem[]>([]);
    const [rawStatuses, setRawStatuses] = useState<RawStatuses | null>(null); // 生のステータスオブジェクト
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<Error | null>(null);

    const loadStatuses = useCallback(async () => {
        try {
            setLoading(true);
            const data = await fetchStatuses();

            if (data && data.data) {
                // 生データを保存
                setRawStatuses(data.data);

                // 予約ステータス: { id: { code, name, ... } } -> [{ id, label, code, ... }]
                if (data.data.reservation) {
                    const list: StatusItem[] = Object.entries(data.data.reservation).map(([id, val]: [string, any]) => ({
                        id: Number(id),
                        label: val.name,
                        code: val.code,
                        bg_color: val.bg_color,
                        text_color: val.text_color
                    })).sort((a, b) => (a.id as number) - (b.id as number));
                    setReservationStatusList(list);
                }

                // 車両ステータス
                if (data.data.vehicle) {
                    const list: StatusItem[] = Object.entries(data.data.vehicle).map(([id, val]: [string, any]) => ({
                        id: Number(id),
                        label: val.name,
                        code: val.code,
                        bg_color: val.bg_color,
                        text_color: val.text_color
                    })).sort((a, b) => (a.id as number) - (b.id as number));
                    setVehicleStatusList(list);
                }
                // 車両位置ステータス
                if (data.data.vehicle_location) {
                    const list: StatusItem[] = Object.entries(data.data.vehicle_location).map(([id, val]: [string, any]) => ({
                        id: Number(id),
                        label: val.name,
                        code: val.code
                    })).sort((a, b) => (a.id as number) - (b.id as number));
                    setVehicleLocationStatusList(list);
                }
                // 車種ステータス/タイプ
                if (data.data.vehicle_type) {
                    const list: StatusItem[] = Object.entries(data.data.vehicle_type).map(([id, val]: [string, any]) => ({
                        id: Number(id),
                        label: val.name,
                        code: val.code,
                        bg_color: val.bg_color,
                        text_color: val.text_color
                    })).sort((a, b) => (a.id as number) - (b.id as number));
                    setVehicleTypeList(list);
                }
            }
        } catch (err) {
            debug.error('useReservationStatuses', 'failed to fetch statuses', err);
            setError(err as Error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadStatuses();
    }, [loadStatuses]);

    // マッピング関数をメモ化
    const getCodeById = useCallback((id: number | string, category: string = 'reservation'): string | null => {
        if (!rawStatuses || !rawStatuses[category]) return null;
        const status = rawStatuses[category]![String(id)];
        return status ? status.code : null;
    }, [rawStatuses]);

    const getIdByCode = useCallback((code: string, category: string = 'reservation'): number | null => {
        if (!rawStatuses || !rawStatuses[category]) return null;
        const entry = Object.entries(rawStatuses[category]!).find(
            ([_, status]) => status.code === code
        );
        return entry ? parseInt(entry[0]) : null;
    }, [rawStatuses]);

    // 生のステータスオブジェクトを提供（オブジェクト形式: { id: { code, name, ... } }）
    const reservationStatuses = useMemo(() => rawStatuses?.reservation ?? {}, [rawStatuses]);
    const vehicleStatuses = useMemo(() => rawStatuses?.vehicle ?? {}, [rawStatuses]);
    const vehicleLocationStatuses = useMemo(() => rawStatuses?.vehicle_location ?? {}, [rawStatuses]);
    const vehicleTypeStatuses = useMemo(() => rawStatuses?.vehicle_type ?? {}, [rawStatuses]);

    return {
        // リスト形式（既存の互換性維持）
        reservationStatusList,
        vehicleStatusList,
        vehicleLocationStatusList,
        vehicleTypeList,
        // オブジェクト形式（新規）
        reservationStatuses,
        vehicleStatuses,
        vehicleLocationStatuses,
        vehicleTypeStatuses,
        // マッピング関数
        getCodeById,
        getIdByCode,
        // その他
        loading,
        error,
        reload: loadStatuses
    };
};
